package com.swetdot.swetgram.ui.moderation

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import com.swetdot.swetgram.data.moderation.ModerationReport
import com.swetdot.swetgram.data.moderation.ReportStatus
import com.swetdot.swetgram.ui.chat.ChatActionRow
import com.swetdot.swetgram.ui.chat.ChatDialog
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetCard
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetListRowSkeleton
import com.swetdot.swetgram.ui.design.SwetPullToRefresh
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.discover.discoverTimestamp
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.profile.ProfileNavRow
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import kotlinx.datetime.TimeZone
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * The moderation queue -- `adminListReports`, newest first.
 *
 * STA §16.1 asks for a panel that can "view reports" and "resolve reports" and warns in the same
 * breath not to build a complex admin product. So this is a list of rows and a filter, and nothing
 * else: no bulk actions, no assignment, no search, no counts. `ModerationViewModel`'s header argues
 * why the panel is in the phone client at all rather than on STA §16.1's web page, and what that
 * costs.
 *
 * IT IS BUILT FROM THE APP'S OWN COMPONENTS AND NOTHING NEW. The filter is a `ProfileNavRow` that
 * opens a `ChatDialog` -- the same action sheet the chat list and the post detail screen use --
 * rather than a chip strip, because the design system has no chip and inventing one for a staff-only
 * screen is the wrong order of operations. SDS §11.6's tabs are the four bottom-nav tabs and nothing
 * else, which is the same argument `GroupScreen` makes for not having a tab bar.
 *
 * WHAT A ROW CANNOT SAY, and it is the whole shape of this screen: the reported CONTENT. `Report`
 * carries a `target_type`, a bare uuid, a reason, an optional line of details from the reporter and
 * a content hash -- and no preview, no title and no author. There is no operation in v1.0.0 that
 * could supply one for a post in a private group the moderator has not joined. STA §16.1 asks for
 * "view reported user/post/message metadata"; the contract declares nothing that serves it. So a row
 * shows what a row can honestly show, and the detail screen says out loud what is missing rather
 * than leaving a moderator to wonder whether it failed to load.
 */
@Composable
fun ModerationQueueScreen(
    state: ModerationQueueUiState,
    onBack: () -> Unit,
    onOpenReport: (ModerationReport) -> Unit,
    onSetFilter: (ReportStatus?) -> Unit,
    onRefresh: () -> Unit,
    onLoadMore: () -> Unit,
    onDismissError: () -> Unit,
    modifier: Modifier = Modifier,
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    var filterMenu by remember { mutableStateOf(false) }
    // One reading for the whole screen, so two rows cannot disagree about what "Today" means.
    // `ChatListScreen`, `DiscoverScreen` and `PostDetailScreen` all take the same `remember`.
    val now = remember { Clock.System.now() }

    Column(modifier.fillMaxSize()) {
        // TODO(localization): every user-facing string in this file moves into resources (EN + RU).
        // Blocked on the `moderation_` keys living in the shared `values/strings.xml` rather than
        // in a per-area file; `ReportFlow.kt` carries the full note.
        SwetTopAppBar(title = "Reports", onBack = onBack)

        if (state.error != null) {
            SwetErrorBanner(
                message = state.error,
                // No Retry over a 403: it is not a failure, it is an answer. See
                // `ModerationQueueUiState.forbidden`.
                onRetry = if (state.forbidden) null else onRefresh,
                onDismiss = onDismissError,
            )
        }

        when {
            // Terminal. Everything else on this screen is a control over a queue this account may
            // not read, and a Try again button would be a button that exists to fail.
            state.forbidden -> SwetEmptyState(
                title = "Not available.",
                body = "This account does not have moderator access.",
                actionText = "Back",
                onAction = onBack,
            )

            else -> {
                ProfileNavRow(
                    title = "Showing",
                    value = queueFilterLabel(state.filter),
                    onClick = { filterMenu = true },
                )

                SwetPullToRefresh(
                    refreshing = state.loading && state.loadedOnce,
                    onRefresh = onRefresh,
                    modifier = Modifier.weight(1f),
                ) {
                    when {
                        // Nothing read yet and nothing to paint from. Skeletons rather than a
                        // spinner -- SDS §2.1 asks for loading screens to be avoided.
                        state.reports.isEmpty() && !state.loadedOnce ->
                            Column(Modifier.fillMaxSize()) {
                                repeat(SKELETON_ROWS) { SwetListRowSkeleton() }
                            }

                        // Read, and genuinely empty. The one state that may say so out loud.
                        state.reports.isEmpty() -> SwetEmptyState(
                            title = "Nothing to review.",
                            body = queueEmptyBody(state.filter),
                        )

                        else -> ReportList(
                            state = state,
                            now = now,
                            zone = zone,
                            onOpenReport = onOpenReport,
                            onLoadMore = onLoadMore,
                        )
                    }
                }
            }
        }
    }

    if (filterMenu) {
        ChatDialog(onDismiss = { filterMenu = false }) {
            Text(
                text = "Show reports that are",
                style = MaterialTheme.typography.titleLarge,
                color = SwetgramThemeTokens.colors.textPrimary,
            )
            Spacer(Modifier.height(Space.two))
            QueueFilters.forEach { filter ->
                ChatActionRow(
                    text = queueFilterLabel(filter),
                    onClick = {
                        filterMenu = false
                        onSetFilter(filter)
                    },
                )
            }
        }
    }
}

/** Enough placeholder rows to fill the first screen without pretending to know the real count. */
private const val SKELETON_ROWS = 6

/** How close to the end of the list a scroll gets before the next page is asked for. */
private const val PAGING_THRESHOLD_ROWS = 4

/**
 * The contract's four `status` values plus "everything".
 *
 * `reviewing` is offered even though NOTHING WRITES IT in v1.0.0 -- `adminGetReport` is a GET and
 * must not have side effects, and `adminResolveReport` goes straight to a terminal state. It is a
 * legal filter value, a moderator can set the column by hand, and a filter that silently omitted one
 * of four declared states would be a filter that hides work.
 */
private val QueueFilters: List<ReportStatus?> = listOf(
    ReportStatus.OPEN,
    ReportStatus.REVIEWING,
    ReportStatus.RESOLVED,
    ReportStatus.REJECTED,
    null,
)

internal fun queueFilterLabel(filter: ReportStatus?): String =
    filter?.let { reportStatusLabel(it) } ?: "Everything"

internal fun queueEmptyBody(filter: ReportStatus?): String = when (filter) {
    ReportStatus.OPEN -> "No open reports."
    null -> "No reports have been filed."
    else -> "No reports are ${queueFilterLabel(filter).lowercase()}."
}

@Composable
private fun ReportList(
    state: ModerationQueueUiState,
    now: Instant,
    zone: TimeZone,
    onOpenReport: (ModerationReport) -> Unit,
    onLoadMore: () -> Unit,
) {
    val listState = rememberLazyListState()

    // Paging by proximity to the end rather than by a footer item entering composition, which is
    // `DiscoverScreen`'s pattern: a footer fires once and then has to be re-entered, and a queue
    // whose next page arrives only after the moderator scrolls past the bottom feels broken.
    LaunchedEffect(listState, state.hasMore) {
        snapshotFlow {
            val last = listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0
            last >= state.reports.size - PAGING_THRESHOLD_ROWS
        }.collect { near -> if (near) onLoadMore() }
    }

    LazyColumn(
        state = listState,
        modifier = Modifier.fillMaxSize().navigationBarsPadding(),
        contentPadding = PaddingValues(Space.two),
        verticalArrangement = Arrangement.spacedBy(Space.one),
    ) {
        items(items = state.reports, key = { it.id }) { report ->
            ReportRow(
                report = report,
                now = now,
                zone = zone,
                onClick = { onOpenReport(report) },
            )
        }
    }
}

/**
 * One report, as a card.
 *
 * NOT `SwetListRow`: that is SDS §12.1's 72dp conversation row with a leading avatar slot and a
 * trailing unread badge, and a report has neither a face nor a count. `GroupPersonRow` and
 * `CommentRow` both record the same refusal for the same component.
 *
 * THE UUID IS SHOWN, in full, and that is deliberate rather than lazy. It is the ONLY handle a
 * moderator has on the reported thing -- there is no preview and no title anywhere in the contract
 * -- so it is what they would paste into a database query, and truncating it would make the one
 * usable field unusable.
 */
@Composable
private fun ReportRow(
    report: ModerationReport,
    now: Instant,
    zone: TimeZone,
    onClick: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors

    SwetCard(onClick = onClick, showBorder = true) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = "${reportTargetLabel(report)} - ${reportReasonLabel(report)}",
                style = MaterialTheme.typography.titleMedium,
                color = colors.textPrimary,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f),
            )
            Spacer(Modifier.width(Space.one))
            Text(
                text = reportStatusLabel(report.status),
                style = MaterialTheme.typography.labelMedium,
                // §4.6's semantics: an open report still needs somebody, a decided one does not.
                // The words say the same thing, so the colour reinforces rather than carries it.
                //
                // WEIGHT RATHER THAN HUE, since `warning` is gone with the amber. Open is the
                // bright one because it is the one with work left in it; decided recedes. That is
                // the same statement the amber was making, in the only vocabulary a monochrome
                // palette has.
                color = if (report.status.isOpen) colors.textPrimary else colors.textSecondary,
                maxLines = 1,
            )
        }

        Spacer(Modifier.height(Space.half))

        Text(
            text = report.targetId,
            style = MaterialTheme.typography.labelSmall,
            color = colors.textSecondary,
            maxLines = 1,
        )

        val details = report.details?.trim().orEmpty()
        if (details.isNotEmpty()) {
            Spacer(Modifier.height(Space.one))
            Text(
                text = details,
                style = MaterialTheme.typography.bodySmall,
                color = colors.textPrimary,
                // Two lines in a queue; the whole thing is on the detail screen. A reporter's
                // paragraph would otherwise make one row taller than the screen.
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        }

        // NULL ONLY WHEN THE DATE COULD NOT BE PARSED, and nothing is drawn then rather than an
        // epoch fallback -- a queue with no day separators has nothing to contradict "01.01.1970".
        val createdAt = report.createdAt
        if (createdAt != null) {
            Spacer(Modifier.height(Space.half))
            Text(
                // `discoverTimestamp` returns a [UiText] since the localization pass: the day word
                // is a resource and the join between it and the clock is one too, so the two halves
                // can be ordered by the language. Resolved here; the rest of this screen is still
                // English, see the file header.
                text = discoverTimestamp(createdAt, now, zone).resolve(),
                style = MaterialTheme.typography.labelSmall,
                color = colors.textSecondary,
                maxLines = 1,
            )
        }
    }
}
